import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, ChevronDown, Facebook, Instagram, Twitter, Dribbble } from "lucide-react";
import api, { imgUrl } from "@/lib/api";
import SEOHead from "@/components/SEOHead";

export default function Home() {
  const [banners, setBanners] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [seo, setSeo] = useState({});
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/banners").then(({ data }) => setBanners(data));
    api.get("/events?limit=6").then(({ data }) => setEvents(data));
    api.get("/products?limit=4").then(({ data }) => setProducts(data));
    api.get("/blogs?limit=6").then(({ data }) => setBlogs(data));
    api.get("/match-results?limit=4").then(({ data }) => setResults(data));
    api.get("/stats").then(({ data }) => setStats(data));
    api.get("/notifications").then(({ data }) => setNotifications(data));
    api.get("/seo/home").then(({ data }) => setSeo(data));
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const id = setInterval(() => setActive((a) => (a + 1) % banners.length), 7000);
    return () => clearInterval(id);
  }, [banners.length]);

  const banner = banners[active];
  const showSide = banner?.show_side_post && (banner?.side_post_title || banner?.side_post_image);

  return (
    <div data-testid="home-page" className="text-white">
      <SEOHead
        title={seo.title || "The Arena — Indoor Cricket Club"}
        description={seo.description || "Indoor cricket arena hosting Premier League, corporate tournaments, private bookings and floodlit finals."}
        keywords={seo.keywords}
        ogImage={seo.og_image}
      />

      {/* ─────────────── HERO ─────────────── */}
      <section className="relative overflow-hidden bg-[var(--pz-ink)]">
        {banner?.image_url && (
          <>
            <img
              src={imgUrl(banner.image_url)}
              alt={banner.title}
              data-testid="hero-banner-image"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className={`absolute inset-0 ${showSide ? "bg-gradient-to-r from-[var(--pz-ink)] via-[var(--pz-ink)]/60 to-transparent" : "bg-gradient-to-b from-[var(--pz-ink)]/80 via-[var(--pz-ink)]/40 to-[var(--pz-ink)]"}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--pz-ink)] via-transparent to-transparent" />
          </>
        )}

        <div className="absolute inset-x-0 bottom-0 grid place-items-center pointer-events-none">
          <div className="pz-watermark text-[22vw] leading-[0.8] -mb-10">cricket</div>
        </div>

        <div className={`relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-20 lg:pt-28 pb-32 lg:pb-40 ${showSide ? "grid lg:grid-cols-12 gap-10 items-end" : "flex flex-col items-center text-center"}`}>
          <div className={`${showSide ? "lg:col-span-7" : "max-w-4xl"} pz-fade-up`}>
            <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-6">Digest</div>
            <h1 className={`font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold uppercase leading-[0.95] tracking-tight ${showSide ? "max-w-4xl" : ""}`}>
              {banner?.title || "Detailed cricket match news & reviews"}
            </h1>
            {banner?.subtitle && (
              <p className={`mt-7 text-zinc-300 text-base ${showSide ? "max-w-lg" : "max-w-lg mx-auto"}`}>
                {banner.subtitle}
              </p>
            )}
            <div className={`mt-9 flex flex-wrap gap-3 ${showSide ? "" : "justify-center"}`}>
              {banner?.cta_label && (
                <Link to={banner?.cta_link || "/events"} data-testid="hero-cta-primary" className="btn-red">
                  {banner.cta_label} <ArrowRight className="h-4 w-4" />
                </Link>
              )}
              {banner?.cta2_label && (
                <Link to={banner?.cta2_link || "/results"} data-testid="hero-cta-secondary" className="btn-outline">
                  {banner.cta2_label}
                </Link>
              )}
            </div>
            {showSide && (
              <div className="mt-10 hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                <span className="h-px w-10 bg-zinc-600" />
                Scroll Down <ChevronDown className="h-3.5 w-3.5" />
              </div>
            )}
          </div>

          {showSide && (
            <aside className="lg:col-span-5 pz-fade-up pz-delay-2">
              <div className="font-display uppercase tracking-[0.2em] text-sm text-white mb-5">{banner.side_post_category || "Recent Post"}</div>
              <Link to={banner.side_post_link || "#"} data-testid="hero-featured-blog" className="block group">
                {banner.side_post_image && (
                  <div className="overflow-hidden rounded-md">
                    <img src={imgUrl(banner.side_post_image)} alt={banner.side_post_title} className="w-full aspect-[4/3] object-cover transition duration-700 group-hover:scale-105" />
                  </div>
                )}
                {banner.side_post_title && (
                  <div className="font-display text-xl md:text-2xl font-bold uppercase mt-4 leading-tight text-white group-hover:text-[var(--pz-red)] transition">{banner.side_post_title}</div>
                )}
              </Link>
              <div className="mt-8 flex items-center gap-4 text-zinc-400">
                <Facebook className="h-4 w-4 hover:text-white cursor-pointer" />
                <Twitter className="h-4 w-4 hover:text-white cursor-pointer" />
                <Dribbble className="h-4 w-4 hover:text-white cursor-pointer" />
                <Instagram className="h-4 w-4 hover:text-white cursor-pointer" />
              </div>
            </aside>
          )}
        </div>

        {banners.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                data-testid={`banner-dot-${i}`}
                onClick={() => setActive(i)}
                className={`h-1.5 rounded-full transition-all ${i === active ? "w-10 bg-[var(--pz-red)]" : "w-2 bg-white/30"}`}
                aria-label={`banner ${i + 1}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* ─────────────── NOTIFICATIONS TICKER ─────────────── */}
      {notifications.length > 0 && (
        <section data-testid="notification-ticker" className="ticker-band py-3 overflow-hidden">
          <div className="pz-marquee whitespace-nowrap flex gap-12">
            {[...notifications, ...notifications].map((n, i) => (
              <Link key={i} to={n.link || "#"} className="flex items-center gap-3 hover:opacity-80">
                <Calendar className="h-3.5 w-3.5" />
                {n.text}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ─────────────── MATCH RESULTS ─────────────── */}
      {results.length > 0 && (
        <section className="bg-[var(--pz-paper)] py-20 text-[var(--pz-ink)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {results.slice(0, 4).map((r, i) => (
                <article key={r.id} data-testid={`match-result-${i}`} className="bg-white rounded-md p-6 shadow-sm border border-zinc-100 pz-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="text-xs text-zinc-500 text-center mb-4">{new Date(r.match_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                  <div className="flex items-center justify-between gap-2 overflow-hidden">
                    <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                      <TeamCircle name={r.team_a_name} logo={r.team_a_logo} />
                      <div className="text-[9px] uppercase tracking-[0.02em] font-bold leading-tight text-center break-all w-full">{r.team_a_name}</div>
                    </div>
                    <div className="font-display text-xl xl:text-2xl font-bold leading-none whitespace-nowrap shrink-0">{r.team_a_score}<span className="mx-1 text-zinc-400">-</span>{r.team_b_score}</div>
                    <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                      <TeamCircle name={r.team_b_name} logo={r.team_b_logo} />
                      <div className="text-[9px] uppercase tracking-[0.02em] font-bold leading-tight text-center break-all w-full">{r.team_b_name}</div>
                    </div>
                  </div>
                  <div className="text-center mt-5 text-xs uppercase tracking-[0.2em] text-zinc-500">{r.league}</div>
                  <div className="text-center mt-1 text-sm text-zinc-600">{r.venue}</div>
                </article>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/results" data-testid="see-all-results" className="btn-outline-dark">All Results <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* ─────────────── STATS COUNTER ─────────────── */}
      {stats.length > 0 && (
        <section className="bg-white py-24 text-[var(--pz-ink)] relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-12 text-center">
            {stats.map((s, i) => (
              <div key={s.id} data-testid={`stat-${i}`} className="pz-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-2">{s.label}</div>
                <div className="font-display text-6xl lg:text-7xl font-bold tracking-tight">{s.value}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─────────────── TRENDING NOW ─────────────── */}
      {blogs.length > 1 && (() => {
        const trendingFeatured = blogs[0];
        const trendingSide = blogs.slice(1, 4);
        return (
        <section className="bg-[var(--pz-paper)] py-24 text-[var(--pz-ink)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-4">Our Articles</div>
            <h2 className="font-display text-5xl md:text-6xl font-bold uppercase leading-none">Trending now</h2>

            <div className="mt-14 grid lg:grid-cols-2 gap-10">
              {trendingFeatured && (
                <Link to={`/blog/${trendingFeatured.slug}`} data-testid="trending-featured" className="group">
                  <div className="relative overflow-hidden rounded-md">
                    {trendingFeatured.cover_image && <img src={imgUrl(trendingFeatured.cover_image)} alt={trendingFeatured.title} className="w-full aspect-[4/3] object-cover transition duration-700 group-hover:scale-105" />}
                    <div className="absolute top-4 left-4">
                      <span className="bg-yellow-400 text-zinc-900 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">{trendingFeatured.category}</span>
                    </div>
                  </div>
                </Link>
              )}
              <div className="space-y-6">
                {trendingSide.map((b, i) => (
                  <Link to={`/blog/${b.slug}`} key={b.id} data-testid={`trending-side-${i}`} className="group flex gap-5 items-start border-b border-zinc-200 pb-6 last:border-0">
                    {b.cover_image && (
                      <div className="overflow-hidden rounded-md shrink-0">
                        <img src={imgUrl(b.cover_image)} alt={b.title} className="h-32 w-44 object-cover transition duration-500 group-hover:scale-105" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                        <span className="text-[var(--pz-red)] font-bold">{b.category}</span>
                        <span>·</span>
                        <span>{new Date(b.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                      </div>
                      <div className="font-display text-xl md:text-2xl font-bold uppercase leading-tight mt-2 group-hover:text-[var(--pz-red)] transition">{b.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-10 text-center">
              <Link to="/blog" className="btn-outline-dark">All Articles <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
        );
      })()}

      {/* ─────────────── EVENTS ─────────────── */}
      {events.length > 0 && (
        <section className="bg-[var(--pz-ink)] py-24 relative overflow-hidden">
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="pz-watermark text-[20vw] leading-none">events</div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
              <div>
                <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-3">Upcoming</div>
                <h2 className="font-display text-5xl md:text-6xl font-bold uppercase text-white leading-none">Events</h2>
              </div>
              <Link to="/events" data-testid="see-all-events" className="btn-outline">All Events <ArrowRight className="h-4 w-4" /></Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.slice(0, 6).map((e, i) => (
                <Link
                  to={`/events/${e.slug}`}
                  key={e.id}
                  data-testid={`home-event-${i}`}
                  className="bg-[var(--pz-ink-2)] hover:bg-[var(--pz-ink-3)] border border-[var(--pz-line)] rounded-md overflow-hidden group transition-all hover:-translate-y-1 pz-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {e.image_url && (
                    <div className="relative overflow-hidden">
                      <img src={imgUrl(e.image_url)} alt={e.title} className="w-full h-52 object-cover transition duration-700 group-hover:scale-105" />
                      <span className="absolute top-3 left-3 bg-[var(--pz-red)] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">{e.event_type}</span>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{new Date(e.start_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                    <h3 className="font-display text-xl font-bold uppercase mt-2 text-white leading-tight group-hover:text-[var(--pz-red)] transition">{e.title}</h3>
                    <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{e.description}</p>
                    {e.price && <div className="mt-4 text-[var(--pz-red)] font-bold text-sm">{e.price}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────── SHOP TEASER ─────────────── */}
      <section className="bg-[var(--pz-paper)] py-24 text-[var(--pz-ink)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-3">Club Store</div>
              <h2 className="font-display text-5xl md:text-6xl font-bold uppercase leading-none">Shop the Arena</h2>
            </div>
            <Link to="/shop" data-testid="see-all-products" className="btn-outline-dark">All Products <ArrowRight className="h-4 w-4" /></Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p, i) => (
              <Link
                to={`/shop/${p.slug}`}
                key={p.id}
                data-testid={`home-product-${i}`}
                className="group bg-white rounded-md overflow-hidden border border-zinc-100 hover:-translate-y-1 transition-all duration-300 pz-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                {p.image_url && (
                  <div className="overflow-hidden">
                    <img src={imgUrl(p.image_url)} alt={p.name} className="w-full aspect-square object-cover transition duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.category}</div>
                  <h3 className="font-display text-lg font-bold uppercase mt-1 group-hover:text-[var(--pz-red)] transition">{p.name}</h3>
                  <div className="mt-3 text-zinc-900 font-bold">LKR {Number(p.price).toLocaleString()}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── CTA STRIP ─────────────── */}
      <section className="bg-[var(--pz-ink)] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--pz-red)] rounded-md p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-72 h-72 bg-white/10 rounded-full" />
            <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-white/10 rounded-full" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-display text-4xl md:text-5xl font-bold uppercase text-white leading-tight">Book the arena. Own the night.</h2>
                <p className="text-white/90 mt-4 max-w-md">Private courts, league entry, corporate sixes — message us on WhatsApp and we'll lock your slot in under an hour.</p>
              </div>
              <div className="flex md:justify-end flex-wrap gap-3">
                <Link to="/contact" data-testid="cta-section-book" className="bg-white text-[var(--pz-ink)] hover:bg-zinc-100 font-bold uppercase tracking-[0.06em] text-sm px-7 py-4 rounded-full transition">Book Now</Link>
                <Link to="/events" className="btn-outline border-white/40 text-white">See Schedule</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function TeamCircle({ name, logo }) {
  return (
    <div className="h-10 w-10 grid place-items-center bg-[var(--pz-ink)] text-white rounded-full shrink-0 overflow-hidden">
      {logo ? <img src={imgUrl(logo)} alt={name} className="h-full w-full object-cover" /> : <span className="font-display text-[10px] uppercase">{name.slice(0, 2)}</span>}
    </div>
  );
}
